import React, { useEffect } from 'react';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Checkbox, CheckboxProps, Icon, Label, Modal } from 'semantic-ui-react';
import { useDispatch, useSelector } from 'react-redux';
import { selectAnnouncementsTeleradiologySeen } from 'app/redux/announcements/reducer';
import { setAnnouncementsTeleradiologySeen } from 'app/redux/announcements/actions';
import { selectAiOnlyEnabledConfiguration } from 'app/redux/aiOnlyConfiguration/reducer';
import { selectLoggedIn, selectSubscribed, selectUsername } from 'app/redux/global/selectors';
import {
  selectPermissionAcquisition,
  selectPermissionTeleradiology,
} from 'app/redux/permissions/reducer';
import ApiCalls from 'app/utils/apiCalls';
import { Integer } from 'type-fest';

type Subscription = {
  _id: string;
  backend_name: string;
  buyDate: string;
  coupon?: string;
  currency?: string;
  currentPeriodEnd: string;
  used?: number;
  price?: number;
  isCancellable?: boolean;
  is_demo?: boolean;
  cancelled?: boolean;
};

const SUBSCRIPTION_DURATION_YEARS = 5;
const DAYS_UNTIL_EXPIRY = 15;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const MS_PER_YEAR = 365.25 * MS_PER_DAY;

type Seller = {
  sex: string;
  firstname: string;
  lastname: string;
  phonenumber: string;
};

/**
 * Map seller sex to translation key for title
 * M = Monsieur (M.), F = Madame (Mme), etc.
 */
function getTitleTranslationKey(sex?: string): string {
  switch (sex?.toUpperCase()) {
    case 'F':
      return 'general.title.miss';
    case 'M':
    default:
      return 'general.title.mister';
  }
}

/**
 * Format seller name with title, firstname, lastname and phone
 * Format: "Title FirstName LastName (PhoneNumber)"
 */
function formatSellerName(seller: Seller, intl: any): string {
  const titleKey = getTitleTranslationKey(seller.sex);
  const titleLabel = intl.formatMessage({ id: titleKey });

  const firstname = seller.firstname;
  const lastname = seller.lastname;
  const phone = `(${seller.phonenumber})`;

  return `${titleLabel} ${firstname} ${lastname} ${phone}`.trim();
}

/**
 * Map seller sex to translation key for title
 * M = Monsieur (M.), F = Madame (Mme), etc.
 */
function getFasRepresentativeTranslationKey(count?: number): string {
  switch (count) {
    case 1:
      return 'announcements.end-of-warranty.content.contact-your-fas-representative';
    default:
      return 'announcements.end-of-warranty.content.contact-your-fas-representatives';
  }
}

/**
 * Check if a subscription should trigger the warranty end notification
 * Conditions:
 * - Subscription duration is 5 years
 * - Subscription expires in 15 days
 */
function shouldShowWarrantyModal(subscription: Subscription): boolean {
  const buyDate = new Date(subscription.buyDate);
  const endDate = new Date(subscription.currentPeriodEnd);
  const now = new Date();

  // Calculate subscription duration in years
  const durationMs = endDate.getTime() - buyDate.getTime();
  const durationYears = durationMs / MS_PER_YEAR;

  // Check if subscription is approximately 5 years (with 0.5 year tolerance)
  const is5YearSubscription = Math.abs(durationYears - SUBSCRIPTION_DURATION_YEARS) < 0.5;

  // Calculate days until expiry
  const daysUntilExpiry = (endDate.getTime() - now.getTime()) / MS_PER_DAY;

  // Check if expiring in 15 days (with 1 day tolerance)
  const expiresIn15Days = daysUntilExpiry <= DAYS_UNTIL_EXPIRY && daysUntilExpiry > 0;
  console.log(
    `Subscription ${subscription.backend_name}: durationYears=${durationYears.toFixed(
      2
    )}, daysUntilExpiry=${daysUntilExpiry.toFixed(
      2
    )}, is5YearSubscription=${is5YearSubscription}, expiresIn15Days=${expiresIn15Days}`
  );

  return is5YearSubscription && expiresIn15Days;
}

export default function EndOfWarrantyAnnouncementModal() {
  const isLoggedIn = useSelector(selectLoggedIn);
  const hasAcquisitionPermission = useSelector(selectPermissionAcquisition);
  const hasAISubscription = useSelector(selectSubscribed);
  const dispatch = useDispatch();
  const intl = useIntl();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [formattedSellers, setFormattedSellers] = useState<string>('');

  // Fetch user subscription data
  useEffect(() => {
    if (isLoggedIn) {
      ApiCalls.getPurchaseHistory()
        .then((response) => {
          const purchaseHistory = response.data.purchaseHistory;
          console.log('Fetched purchase history:', purchaseHistory);
          if (purchaseHistory && purchaseHistory.length > 0 && purchaseHistory[0].subscription) {
            setSubscriptions(purchaseHistory[0].subscription);
          }
        })
        .catch((error) => {
          console.error('Failed to fetch purchase history:', error);
        });

      // Try to fetch current user info via userDetails endpoint which already exists
      ApiCalls.getRegionalSalesContacts()
        .then((response) => {
          const contactsList = response.data?.sellers || [];
          setSellers(contactsList);

          // Format seller names with title, firstname, lastname and phone
          const formattedList = contactsList
            .map((seller: Seller) => formatSellerName(seller, intl))
            .join(', ');
          setFormattedSellers(formattedList);
        })
        .catch((error) => {
          console.error('Failed to fetch regional sales contacts:', error);
        });
    }
  }, [isLoggedIn]);

  // Determine if modal should be open
  useEffect(() => {
    if (!isLoggedIn) {
      setModalOpen(false);
      return;
    }

    // Check if user has any subscription that matches the warranty end condition
    const hasWarrantyEndingSubscription = subscriptions.some(shouldShowWarrantyModal);

    // Show modal if user has acquisition/AI subscription AND has a warranty ending subscription
    const shouldShow =
      (hasAcquisitionPermission || hasAISubscription) && hasWarrantyEndingSubscription;

    setModalOpen(shouldShow);
  }, [isLoggedIn, subscriptions, hasAcquisitionPermission, hasAISubscription]);

  return (
    <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
      <Modal.Header>
        <h3 style={{ color: '#db2828' }}>
          <Icon name="attention" />
          <FormattedMessage id="announcements.end-of-warranty.title" />
        </h3>
      </Modal.Header>
      <Modal.Content>
        <p>
          <FormattedMessage id="announcements.end-of-warranty.content.intro" />
        </p>

        <p>
          <FormattedMessage
            id={getFasRepresentativeTranslationKey(sellers.length)}
            values={{ fas_sales_representatives: formattedSellers }}
          />
        </p>
        <p>
          <FormattedMessage id="announcements.end-of-warranty.content.outro" />
        </p>
      </Modal.Content>
      <Modal.Actions>
        <Button negative onClick={() => setModalOpen(false)}>
          <FormattedMessage id="close" />
        </Button>
      </Modal.Actions>
    </Modal>
  );
}
